import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionAlternativeLike {
  transcript?: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0?: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface WindowWithSpeech extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as WindowWithSpeech;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseDictationOptions {
  /** Called with the live transcript (interim + final) as the user speaks. */
  onTranscript: (text: string) => void;
  /** Called once when recognition stops cleanly. Receives the final transcript. */
  onFinal?: (text: string) => void;
  /** Called when recognition errors. */
  onError?: (err: string) => void;
  lang?: string;
}

export function useDictation({ onTranscript, onFinal, onError, lang }: UseDictationOptions) {
  const Ctor = getSpeechRecognitionCtor();
  const supported = !!Ctor;
  const [recording, setRecording] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const baseRef = useRef<string>('');
  const finalRef = useRef<string>('');

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* ignore */ }
  }, []);

  const start = useCallback((initialText = '') => {
    if (!Ctor) {
      onError?.('Speech recognition not supported in this browser.');
      return;
    }
    if (recRef.current) {
      stop();
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang ?? navigator.language;
    if (!rec.lang) rec.lang = 'en-US';
    baseRef.current = initialText ? initialText.replace(/\s*$/, ' ') : '';
    finalRef.current = '';

    rec.onresult = (e: SpeechRecognitionEventLike) => {
      let interim = '';
      let finalChunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r[0]?.transcript ?? '';
        if (r.isFinal) finalChunk += t;
        else interim += t;
      }
      if (finalChunk) finalRef.current += finalChunk;
      const combined = (baseRef.current + finalRef.current + interim).replace(/\s+/g, ' ').trimStart();
      onTranscript(combined);
    };
    rec.onerror = (e: { error?: string }) => {
      const msg = e.error ?? 'speech-error';
      if (msg !== 'aborted' && msg !== 'no-speech') onError?.(msg);
    };
    rec.onend = () => {
      const combined = (baseRef.current + finalRef.current).replace(/\s+/g, ' ').trim();
      onFinal?.(combined);
      recRef.current = null;
      setRecording(false);
    };

    try {
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'mic-start-failed';
      onError?.(message);
      setRecording(false);
    }
  }, [Ctor, lang, onTranscript, onFinal, onError, stop]);

  const toggle = useCallback((initialText = '') => {
    if (recording) stop();
    else start(initialText);
  }, [recording, start, stop]);

  useEffect(() => () => {
    const rec = recRef.current;
    if (rec) { try { rec.abort(); } catch { /* ignore */ } }
  }, []);

  return { supported, recording, start, stop, toggle };
}
